/**
 * BridgeAPI v2 — only expose blocks with webmcp.expose=true.
 *
 * v2 Bridge enforces stateContract: exposed tools can only access
 * symbols declared in the block's stateContract.outputSymbols.
 */

import type { BlockDef, Flow } from "@cogo/types-v2"
import type { ActionFlowEngine } from "@cogo/runtime-v2"

interface ModelContextTool {
  name: string
  description: string
  inputSchema?: Record<string, unknown>
  readOnly?: boolean
  execute(params: Record<string, unknown>): Promise<{
    content: Array<{ type: string; text: string }>
  }>
}

interface ModelContext {
  registerTool(tool: ModelContextTool): void
}

declare global {
  interface Navigator {
    modelContext?: ModelContext
  }
}

/**
 * Register v2 block flows as WebMCP tools.
 *
 * Only blocks with meta.webmcp.expose=true are registered.
 * Each tool's inputSchema is derived from stateContract.inputSymbols.
 */
export function registerBridgeToolsV2(
  blocks: Record<string, BlockDef>,
  actionEngines: Record<string, ActionFlowEngine>,
): void {
  if (typeof navigator === "undefined" || !navigator.modelContext) return

  const mc = navigator.modelContext

  for (const [blockName, block] of Object.entries(blocks)) {
    const meta = block.meta
    if (!meta?.webmcp?.expose) continue

    const ns = meta?.namespace ?? meta?.name ?? blockName
    const engine = actionEngines[blockName]
    if (!engine) continue

    for (const [toolName, toolDef] of Object.entries(meta.webmcp.tools)) {
      const flowKey = `${ns}.${toolName}`
      const flow = engine.findFlow(flowKey)
      if (!flow) continue

      // Build inputSchema from stateContract.inputSymbols
      const inputProps: Record<string, unknown> = {}
      for (const sym of meta.stateContract.inputSymbols) {
        inputProps[sym.name] = { type: sym.type, description: `Input: ${sym.path}` }
      }

      mc.registerTool({
        name: flowKey,
        description: toolDef.description,
        inputSchema: Object.keys(inputProps).length > 0
          ? { type: "object", properties: inputProps }
          : undefined,
        readOnly: toolDef.readOnly,
        async execute(params) {
          try {
            await engine.executeFlow(flow as Flow, { event: params })
            return {
              content: [{ type: "text", text: JSON.stringify({ success: true }) }],
            }
          } catch (err) {
            return {
              content: [{ type: "text", text: JSON.stringify({ error: String(err) }) }],
            }
          }
        },
      })
    }
  }
}
